import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Tag, X, Copy, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "tcsw_promo_dismissed_v1";

/**
 * Sticky promo strip shown at the top of the Shop landing.
 * - Fetches /api/checkout/coupons/featured (returns { featured: null } when no coupon is featured)
 * - Hides if no active featured coupon OR user has dismissed it (per-code).
 * - Click the code to copy → toast-less feedback via inline check icon.
 */
export default function ShopPromoBanner() {
  const [promo, setPromo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "null"); } catch { return null; }
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/checkout/coupons/featured`).then(({ data }) => {
      if (!cancelled) setPromo(data.featured || null);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!promo || dismissed === promo.code) return null;

  const discountLabel = promo.kind === "percent"
    ? `${Number(promo.value)}% OFF`
    : `$${Number(promo.value).toFixed(2)} OFF`;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(promo.code));
    setDismissed(promo.code);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* noop */ }
  };

  return (
    <div
      data-testid="shop-promo-banner"
      className="relative z-[60] bg-gradient-to-r from-gold/95 via-gold to-gold/95 text-black px-4 py-2.5 shadow-lg"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 md:gap-6 flex-wrap text-[13px] md:text-sm">
        <span className="inline-flex items-center gap-2 font-semibold">
          <Tag size={14} className="shrink-0" />
          New here? Save {discountLabel} on your first order.
        </span>
        <button
          onClick={copyCode}
          data-testid="shop-promo-copy"
          className="inline-flex items-center gap-2 bg-black/85 text-gold px-3 py-1 rounded-sm font-mono uppercase tracking-[0.15em] text-[12px] hover:bg-black transition-colors"
          title="Click to copy code"
        >
          {promo.code}
          {copied ? <CheckCircle2 size={13} /> : <Copy size={11} className="opacity-70" />}
        </button>
        <Link
          to="/shop"
          data-testid="shop-promo-cta"
          className="hidden md:inline-block text-[11px] uppercase tracking-[0.2em] underline underline-offset-4 hover:no-underline"
        >
          Shop now →
        </Link>
      </div>
      <button
        onClick={dismiss}
        data-testid="shop-promo-dismiss"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-black/60 hover:text-black transition-colors p-1"
        aria-label="Dismiss promo"
      >
        <X size={14} />
      </button>
    </div>
  );
}
